// src/pages/VoucherOverview/VoucherOverviewPage.js
//
// Backoffice "Voucher overview" screen.
// =============================================================================
//
// RESPONSIBILITIES
// ----------------
// - Fetch and list vouchers with filters, pagination, totals, and resizable columns.
// - Show redeem history inline, expandable per voucher row.
// - Toggle the `to_note` flag with optional propagation to existing redeems.
// - Open an HTML preview via VoucherPreview and then allow opening a server PDF.
//
// IMPORTANT RENDER MODEL
// ----------------------
// Preview uses:
//
//   mapVoucherRowToPreview(voucherRow)
//
// as the canonical frontend preview model.
//
// This keeps the admin preview aligned with:
// - the public/frontend voucher preview contract,
// - the backend PDF render pipeline,
// - the package/voucher template-family split.
//
// BUSINESS MODE UPDATE: VOUCHER + PACKAGE SUPPORT
// ------------------------------------------------
// This overview page still belongs to the broader vouchers domain in routing and
// data access. However, rows can now represent two business families:
//
//   1) classic voucher rows
//   2) package rows
//
// Why this matters:
// - backend PDF templates are split into physical families:
//     backend/pdf/templates/voucher/*
//     backend/pdf/templates/package/*
// - VoucherPage supports category "Pakiet"
// - therefore overview / preview / print UX must not assume that every row is a
//   classic voucher.
//
// This page does NOT decide backend template routing.
// Backend PDF generation remains responsible for final family/template routing.
//
// PAGINATION ARCHITECTURE
// -----------------------
// VoucherOverview uses the shared pagination stack:
//
//   src/hooks/useClientPagination.js
//   src/components/common/PaginationFooter.jsx
//   src/components/common/PaginationFooter.css
//
// IMPORTANT:
// - Do NOT modify useClientPagination.js for this page.
// - The stable hook contract is `paginatedItems`.
// - Do NOT destructure alternative names like pageItems/currentItems/items.
// - Pagination is a normal flex child inside `.vo-table-wrap`.
// - The table scroller consumes the remaining height above the footer.
// - There is no fixed footer, no JS footer measurement, and no scroll spacer.
//
// TABLE SCROLL MODEL
// ------------------
// Sticky <thead> in Safari/WebViews is most reliable when both axes are owned by
// one scroller.
//
// Therefore:
// - `.vo-table-scroller` owns vertical and horizontal scrolling.
// - `.vo-table-x` is only a width wrapper and is NOT a scroller.
//
// QR FLOW
// -------
// QR mode performs a dedicated lookup by exact qr_payload.
// While QR mode is active:
// - other filters remain visible for context,
// - other filters are ignored,
// - clearing QR exits QR mode.

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import {
  fetchVouchers,
  archiveVoucher,
  unarchiveVoucher,
  getVoucherEmailDefaults,
  sendVoucherEmail,
  fetchRedeemHistory,
  updateVoucherToNote,
  downloadVoucherPdf,
} from "../../services/voucherService";
import { fetchUsers } from "../../services/userService";
import { VoucherPreview } from "../../components/VoucherPreview";
import { mapVoucherRowToPreview } from "../../utils/mapVoucherRowToPreview";
import { isRequestCanceled } from "../../services/httpUtils";
import useClientPagination from "../../hooks/useClientPagination";
import PaginationFooter from "../../components/common/PaginationFooter";
import AuthContext from "../../context/AuthContext";

import "./VoucherOverviewPage.css";

/* -------------------------------------------------------------------------- */
/* Local formatting helpers                                                    */
/* -------------------------------------------------------------------------- */

/** Simple date formatter used in table cells, filter chips and preview sidebar. */
function formatDate(val) {
  if (!val) return "";
  const d = new Date(val);
  return Number.isNaN(d.getTime()) ? String(val) : d.toLocaleDateString();
}

/**
 * Debounce hook used for free-text filters.
 *
 * Date filters are handled separately with immediate fetch semantics because
 * users expect date picker changes to apply instantly.
 */
function useDebouncedValue(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);

  return debounced;
}

/**
 * Detect empty redeem-history responses.
 *
 * The UI treats these as "no rows" instead of surfacing an error banner.
 */
function isEmptyHistoryError(err) {
  const status = err?.status ?? err?.response?.status;
  if (status === 204 || status === 404) return true;

  const msg = String(err?.message || "").toLowerCase();
  return msg.includes("no redeem history") || msg.includes("no content");
}

/** Totals still assume PLN as reporting currency for management overview. */
function fmtAmount(n) {
  const x = Number(n);
  return Number.isFinite(x) ? `${x.toFixed(2)} PLN` : "—";
}

function isNonEmpty(v) {
  if (Array.isArray(v)) return v.length > 0;
  return Boolean(String(v ?? "").trim());
}

function formatDateChip(val) {
  if (!val) return "";
  return String(val);
}

/* -------------------------------------------------------------------------- */
/* Business-mode helpers                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Resolve business mode from a voucher-domain row.
 *
 * Preferred sources:
 * 1) explicit row.businessMode if already available,
 * 2) category === "Pakiet" => package,
 * 3) fallback => voucher.
 */
function getBusinessModeFromVoucherRow(row) {
  const explicit = String(row?.businessMode || "")
    .trim()
    .toLowerCase();

  if (explicit === "package") return "package";
  if (explicit === "voucher") return "voucher";

  return row?.category === "Pakiet" ? "package" : "voucher";
}

/**
 * UI labels for modal copy.
 *
 * Routing and data access remain in vouchers domain, but admin copy should be
 * business-aware.
 */
function getEntityUiMeta(row, t) {
  const mode = getBusinessModeFromVoucherRow(row);

  if (mode === "package") {
    const singular = t("vouchers:entity.package.singular", {
      defaultValue: "Package",
    });
    return {
      businessMode: "package",
      singular,
      singularLower: t("vouchers:entity.package.lowerSingular", {
        defaultValue: "package",
      }),
      previewTitle: t("vouchers:entity.package.singular", {
        defaultValue: "Package",
      }),
      previewAriaLabel: t("vouchers:overview.packagePreviewAriaLabel", {
        defaultValue: "Package preview",
      }),
    };
  }

  const singular = t("vouchers:entity.voucher.singular", {
    defaultValue: "Voucher",
  });
  return {
    businessMode: "voucher",
    singular,
    singularLower: t("vouchers:entity.voucher.lowerSingular", {
      defaultValue: "voucher",
    }),
    previewTitle: t("vouchers:entity.voucher.singular", {
      defaultValue: "Voucher",
    }),
    previewAriaLabel: t("vouchers:overview.voucherPreviewAriaLabel", {
      defaultValue: "Voucher preview",
    }),
  };
}

/* -------------------------------------------------------------------------- */
/* Table columns and storage keys                                              */
/* -------------------------------------------------------------------------- */

const COL_KEYS = [
  "voucherNo",
  "voucherSearchNo",
  "category",
  "type",
  "issuer",
  "value",
  "remaining",
  "created",
  "expires",
  "status",
  "toNote",
  "comment",
  "edit",
  "redeem",
  "print",
  "history",
  "moreActions",
];

const WIDTHS_STORAGE_KEY = "vo_col_widths";
const STATUS_STORAGE_KEY = "vo_statuses";
const PAGE_SIZE_STORAGE_KEY = "vo_page_size";
const ALL_STATUSES = ["Active", "Archived", "Draft", "Cancelled"];

const MIN_COL_W = 24;
const MAX_AUTOFIT_W = 560;

function loadWidths() {
  try {
    const raw = localStorage.getItem(WIDTHS_STORAGE_KEY);
    if (!raw) return {};

    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed ? parsed : {};
  } catch {
    return {};
  }
}

function saveWidths(widths) {
  try {
    localStorage.setItem(WIDTHS_STORAGE_KEY, JSON.stringify(widths));
  } catch {
    // Ignore storage errors, for example private mode or quota issues.
  }
}

/** Borrow computed font from the rendered table for accurate canvas measurement. */
function getCanvasFontFromElement(el) {
  const cs = window.getComputedStyle(el);
  const fontStyle = cs.fontStyle || "normal";
  const fontVariant = cs.fontVariant || "normal";
  const fontWeight = cs.fontWeight || "400";
  const fontSize = cs.fontSize || "12px";
  const fontFamily =
    cs.fontFamily ||
    'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

  return `${fontStyle} ${fontVariant} ${fontWeight} ${fontSize} ${fontFamily}`;
}

/**
 * Estimate a good width for a column based on header text and visible row values.
 *
 * This is used for double-click auto-fit and for the "{t("vouchers:overview.autoFitColumns")}" button.
 */
function estimateColumnWidth(key, headerLabel, rows, tableEl) {
  if (key === "edit") return 74;
  if (key === "redeem") return 88;
  if (key === "print") return 74;
  if (key === "history") return 118;
  if (key === "moreActions") return 96;

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  ctx.font = tableEl
    ? getCanvasFontFromElement(tableEl)
    : 'normal normal 400 12px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

  const getText = (k, r) => {
    switch (k) {
      case "voucherNo":
        return String(r.voucher_no ?? "");
      case "voucherSearchNo":
        return String(r.voucher_search_no ?? "");
      case "category":
        return String(r.category ?? "");
      case "type":
        return String(r.type ?? "");
      case "issuer":
        return String(r.issuer_id ?? "");
      case "value": {
        const cur = r.currency || "PLN";
        return r.value != null ? `${Number(r.value).toFixed(2)} ${cur}` : "";
      }
      case "remaining": {
        const cur = r.currency || "PLN";
        return r.remaining_balance != null
          ? `${Number(r.remaining_balance).toFixed(2)} ${cur}`
          : "";
      }
      case "created":
        return formatDate(r.createdAt);
      case "expires":
        return formatDate(r.expiration_date);
      case "status":
        return String(r.status ?? (r.open ? "Active" : "Archived"));
      case "toNote":
        return r.to_note ? "Yes" : "No";
      case "comment":
        return String(r.comment ?? "");
      case "edit":
        return "Edit";
      case "redeem":
        return "Redeem";
      case "print":
        return "Print";
      case "history":
        return "History";
      case "moreActions":
        return "More";
      default:
        return "";
    }
  };

  let max = ctx.measureText(headerLabel || key).width;

  for (const r of rows) {
    const w = ctx.measureText(getText(key, r)).width;
    if (w > max) max = w;
  }

  let width = Math.ceil(max + 16 + 6 + 12);

  const minimums = {
    voucherNo: 120,
    voucherSearchNo: 140,
    issuer: 160,
    created: 110,
    expires: 110,
    value: 110,
    remaining: 120,
    status: 110,
    toNote: 100,
    comment: 200,
  };

  if (minimums[key]) width = Math.max(width, minimums[key]);

  return Math.max(MIN_COL_W, Math.min(width, MAX_AUTOFIT_W));
}

/**
 * Fields that should fetch immediately.
 *
 * Rationale:
 * - Date inputs are decision inputs.
 * - Users expect date picker changes to apply immediately.
 * - Some browser date controls behave differently with debounce.
 */
const IMMEDIATE_FETCH_FIELDS = new Set([
  "createdAtFrom",
  "createdAtTo",
  "expirationDateFrom",
  "expirationDateTo",
  "redeemCreatedAtFrom",
  "redeemCreatedAtTo",
]);

/* -------------------------------------------------------------------------- */
/* URL state helpers                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Default filter state for VoucherOverview.
 *
 * IMPORTANT:
 * Keep this object as the single source of truth for an empty filter state.
 *
 * Why:
 * - resetFilters uses it,
 * - URL initialization uses it,
 * - URL serialization compares current values against defaults.
 */
const DEFAULT_VOUCHER_OVERVIEW_FILTERS = {
  voucherNo: "",
  voucherSearchNo: "",
  createdAtFrom: "",
  createdAtTo: "",
  expirationDateFrom: "",
  expirationDateTo: "",
  userIds: [],
  type: "",
  redeemCreatedAtFrom: "",
  redeemCreatedAtTo: "",
  qrPayload: "",
};

const DEFAULT_VOUCHER_OVERVIEW_STATUSES = ["Active"];

/**
 * URL parameter names used by VoucherOverview.
 *
 * We keep names readable instead of very short aliases.
 * This makes copied URLs easier to understand during support/debugging.
 */
const URL_PARAM = {
  voucherNo: "voucherNo",
  voucherSearchNo: "voucherSearchNo",
  createdAtFrom: "createdAtFrom",
  createdAtTo: "createdAtTo",
  expirationDateFrom: "expirationDateFrom",
  expirationDateTo: "expirationDateTo",
  userIds: "userIds",
  type: "type",
  redeemCreatedAtFrom: "redeemCreatedAtFrom",
  redeemCreatedAtTo: "redeemCreatedAtTo",
  qrPayload: "qrPayload",
  statuses: "statuses",
  page: "page",
  pageSize: "pageSize",
};

/**
 * Read a positive integer from URLSearchParams.
 */
function readPositiveIntParam(params, name, fallback) {
  const raw = params.get(name);
  const n = Number(raw);

  if (!Number.isFinite(n) || n <= 0) {
    return fallback;
  }

  return Math.trunc(n);
}

/**
 * Read a comma-separated URL parameter into an array.
 *
 * Example:
 *   ?statuses=Active,Draft
 *   -> ["Active", "Draft"]
 */
function readCsvParam(params, name) {
  const raw = String(params.get(name) || "").trim();

  if (!raw) {
    return [];
  }

  return raw
    .split(",")
    .map((part) => decodeURIComponent(String(part || "").trim()))
    .filter(Boolean);
}

/**
 * Build initial filter state from current URL.
 *
 * This is intentionally called from the useState initializer.
 * That means the first render already uses URL state and there is no visible
 * "reset to default and then jump to URL state" flicker.
 */
function readVoucherOverviewFiltersFromUrl(search) {
  const params = new URLSearchParams(search || "");

  return {
    ...DEFAULT_VOUCHER_OVERVIEW_FILTERS,

    voucherNo: params.get(URL_PARAM.voucherNo) || "",
    voucherSearchNo: params.get(URL_PARAM.voucherSearchNo) || "",

    createdAtFrom: params.get(URL_PARAM.createdAtFrom) || "",
    createdAtTo: params.get(URL_PARAM.createdAtTo) || "",

    expirationDateFrom: params.get(URL_PARAM.expirationDateFrom) || "",
    expirationDateTo: params.get(URL_PARAM.expirationDateTo) || "",

    userIds: readCsvParam(params, URL_PARAM.userIds),

    type: params.get(URL_PARAM.type) || "",

    redeemCreatedAtFrom: params.get(URL_PARAM.redeemCreatedAtFrom) || "",
    redeemCreatedAtTo: params.get(URL_PARAM.redeemCreatedAtTo) || "",

    qrPayload: params.get(URL_PARAM.qrPayload) || "",
  };
}

/**
 * Build initial status selection from current URL.
 *
 * If no status parameter exists, the historical default remains:
 *   ["Active"]
 */
function readVoucherOverviewStatusesFromUrl(search) {
  const params = new URLSearchParams(search || "");
  const values = readCsvParam(params, URL_PARAM.statuses).filter((s) =>
    ALL_STATUSES.includes(s)
  );

  return values.length ? values : DEFAULT_VOUCHER_OVERVIEW_STATUSES;
}

/**
 * Build URLSearchParams from the current overview state.
 *
 * Policy:
 * - default values are omitted from the URL,
 * - non-empty filters are written,
 * - page is written only when > 1,
 * - pageSize is written because users often explicitly choose it,
 * - default status ["Active"] is omitted to keep URLs clean.
 */
function buildVoucherOverviewSearchParams({
  filters,
  selectedStatuses,
  page,
  pageSize,
}) {
  const params = new URLSearchParams();

  const addIfNonEmpty = (name, value) => {
    const text = String(value ?? "").trim();
    if (text) {
      params.set(name, text);
    }
  };

  addIfNonEmpty(URL_PARAM.voucherNo, filters.voucherNo);
  addIfNonEmpty(URL_PARAM.voucherSearchNo, filters.voucherSearchNo);

  addIfNonEmpty(URL_PARAM.createdAtFrom, filters.createdAtFrom);
  addIfNonEmpty(URL_PARAM.createdAtTo, filters.createdAtTo);

  addIfNonEmpty(URL_PARAM.expirationDateFrom, filters.expirationDateFrom);
  addIfNonEmpty(URL_PARAM.expirationDateTo, filters.expirationDateTo);

  if (Array.isArray(filters.userIds) && filters.userIds.length) {
    params.set(URL_PARAM.userIds, filters.userIds.join(","));
  }

  addIfNonEmpty(URL_PARAM.type, filters.type);

  addIfNonEmpty(URL_PARAM.redeemCreatedAtFrom, filters.redeemCreatedAtFrom);
  addIfNonEmpty(URL_PARAM.redeemCreatedAtTo, filters.redeemCreatedAtTo);

  addIfNonEmpty(URL_PARAM.qrPayload, filters.qrPayload);

  const statuses = Array.isArray(selectedStatuses) ? selectedStatuses : [];
  const isDefaultStatus =
    statuses.length === 1 && statuses[0] === DEFAULT_VOUCHER_OVERVIEW_STATUSES[0];

  if (!isDefaultStatus) {
    params.set(URL_PARAM.statuses, statuses.join(","));
  }

  const pageNo = Number(page);
  if (Number.isFinite(pageNo) && pageNo > 1) {
    params.set(URL_PARAM.page, String(Math.trunc(pageNo)));
  }

  const pageSizeNo = Number(pageSize);
  if (Number.isFinite(pageSizeNo) && pageSizeNo > 0) {
    params.set(URL_PARAM.pageSize, String(Math.trunc(pageSizeNo)));
  }

  return params;
}

/**
 * Build a full return path for detail/action pages.
 *
 * This helper intentionally builds the return target from the current React
 * state instead of reusing location.search.
 *
 * Why this matters:
 * - URL synchronization happens in a useEffect,
 * - useEffect can run slightly after the latest state update,
 * - a user may type a filter and immediately click Edit/Redeem,
 * - in that case location.search could theoretically still contain the previous
 *   query string.
 *
 * By using filters/status/page/pageSize directly, returnTo always represents
 * the actual current overview state at the moment the user clicks an action.
 *
 * Example output:
 *   /vouchers/overview?voucherNo=K%2F25%2F12%2F04&page=2&pageSize=50
 */
function buildVoucherOverviewReturnToPath({
  pathname,
  filters,
  selectedStatuses,
  page,
  pageSize,
}) {
  const params = buildVoucherOverviewSearchParams({
    filters,
    selectedStatuses,
    page,
    pageSize,
  });

  const search = params.toString();

  return search ? `${pathname}?${search}` : pathname;
}

/* -------------------------------------------------------------------------- */
/* Main component                                                              */
/* -------------------------------------------------------------------------- */

export default function VoucherOverviewPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation(["common", "vouchers"]);
  const { authState } = useContext(AuthContext);
  // i18n note:
  // VoucherOverview is still a backoffice voucher-domain screen, but rows may
  // represent either classic vouchers or packages. Translation keys therefore
  // live in the vouchers namespace, while technical status/category values
  // returned by the backend remain unchanged.


  /* ------------------------------------------------------------------------ */
  /* Data state                                                                */
  /* ------------------------------------------------------------------------ */

  const [serverVouchers, setServerVouchers] = useState([]);
  const [qrVouchers, setQrVouchers] = useState([]);
  const [users, setUsers] = useState([]);

  const [redeemHistory, setRedeemHistory] = useState({});
  const [historyCount, setHistoryCount] = useState({});
  const [expandedVoucherId, setExpandedVoucherId] = useState(null);
  const [expandedMoreActionsVoucherId, setExpandedMoreActionsVoucherId] = useState(null);

  const [archivingId, setArchivingId] = useState(null);
  const [unarchivingId, setUnarchivingId] = useState(null);
  const [updatingToNoteId, setUpdatingToNoteId] = useState(null);

  /* ------------------------------------------------------------------------ */
  /* Manual voucher e-mail dialog                                             */
  /* ------------------------------------------------------------------------ */

  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailVoucher, setEmailVoucher] = useState(null);
  const [emailTo, setEmailTo] = useState("");
  const [emailCc, setEmailCc] = useState("");
  const [emailAttachPdf, setEmailAttachPdf] = useState(true);
  const [emailDefaultsLoading, setEmailDefaultsLoading] = useState(false);
  const [sendingEmailId, setSendingEmailId] = useState(null);

  /* ------------------------------------------------------------------------ */
  /* Preview / print modal                                                     */
  /* ------------------------------------------------------------------------ */

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewVoucherRow, setPreviewVoucherRow] = useState(null);
  const [printing, setPrinting] = useState(false);

  /* ------------------------------------------------------------------------ */
  /* UI / filters                                                              */
  /* ------------------------------------------------------------------------ */

  const [filtersOpen, setFiltersOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [qrLoading, setQrLoading] = useState(false);

  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [qrBannerMsg, setQrBannerMsg] = useState("");

  /**
   * Filter state is initialized from URL.
   *
   * This fixes the practical user problem:
   * - user filters voucher overview,
   * - opens one voucher,
   * - comes back,
   * - the same filter state is restored.
   */
  const [filters, setFilters] = useState(() =>
    readVoucherOverviewFiltersFromUrl(window.location.search)
  );

  /**
   * Administrative voucher corrections must still be protected by the backend.
   *
   * The frontend uses the role only to avoid showing a misleading restore action
   * to users who cannot successfully call the superuser-only endpoint anyway.
   */
  const isSuperuser =
    String(authState?.user?.role || "").trim().toLowerCase() === "superuser";

  const isQrMode = useMemo(
    () => Boolean(String(filters.qrPayload || "").trim()),
    [filters.qrPayload]
  );

  /**
   * Status state is initialized from URL first.
   *
   * We intentionally no longer use localStorage as the primary source here,
   * because URL state must win when the user returns from edit mode or shares
   * a filtered overview link.
   */
  const [selectedStatuses, setSelectedStatuses] = useState(() =>
    readVoucherOverviewStatusesFromUrl(window.location.search)
  );

  useEffect(() => {
    try {
      localStorage.setItem(STATUS_STORAGE_KEY, JSON.stringify(selectedStatuses));
    } catch {
      // Ignore storage errors.
    }
  }, [selectedStatuses]);

  useEffect(() => {
    if (!errorMsg && !successMsg && !qrBannerMsg) return;

    const id = setTimeout(() => {
      setErrorMsg("");
      setSuccessMsg("");
      setQrBannerMsg("");
    }, 6000);

    return () => clearTimeout(id);
  }, [errorMsg, successMsg, qrBannerMsg]);

  /* ------------------------------------------------------------------------ */
  /* Pagination initial value                                                   */
  /* ------------------------------------------------------------------------ */

  /**
   * Initial page size is also URL-aware.
   *
   * Priority:
   * 1) URL pageSize,
   * 2) previous local preference,
   * 3) default 50.
   */
  const initialPageSize = useMemo(() => {
    const params = new URLSearchParams(window.location.search || "");
    const fromUrl = readPositiveIntParam(params, URL_PARAM.pageSize, null);

    if (fromUrl) {
      return fromUrl;
    }

    const saved = Number(localStorage.getItem(PAGE_SIZE_STORAGE_KEY));
    return Number.isFinite(saved) && saved > 0 ? saved : 50;
  }, []);

  /**
   * Initial page number from URL.
   *
   * useClientPagination currently receives only initialPageSize.
   * Therefore the initial page itself is restored after the hook is created.
   */
  const initialPageFromUrl = useMemo(() => {
    const params = new URLSearchParams(window.location.search || "");
    return readPositiveIntParam(params, URL_PARAM.page, 1);
  }, []);

  /* ------------------------------------------------------------------------ */
  /* Refs                                                                      */
  /* ------------------------------------------------------------------------ */

  const tableRef = useRef(null);
  const tableWidthRef = useRef(null);
  const scrollerRef = useRef(null);
  const qrInputRef = useRef(null);
  const quickSearchRef = useRef(null);
  const scanActiveRef = useRef(false);

  const lastChangedFieldRef = useRef(null);
  const skipNextDebouncedFetchRef = useRef(false);

  /* ------------------------------------------------------------------------ */
  /* Mobile/WebView horizontal touch fallback                                   */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const isCoarse =
      window.matchMedia?.("(pointer: coarse)").matches ||
      window.matchMedia?.("(hover: none)").matches;

    if (!isCoarse) return;

    let active = false;
    let locked = false;
    let startX = 0;
    let startY = 0;
    let startLeft = 0;

    const THRESHOLD = 6;

    const onTouchStart = (e) => {
      if (!e.touches || e.touches.length !== 1) return;

      const t = e.touches[0];
      active = true;
      locked = false;
      startX = t.clientX;
      startY = t.clientY;
      startLeft = scroller.scrollLeft;
    };

    const onTouchMove = (e) => {
      if (!active || !e.touches || e.touches.length !== 1) return;

      const t = e.touches[0];
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;

      if (!locked) {
        if (Math.abs(dx) < THRESHOLD && Math.abs(dy) < THRESHOLD) return;
        locked = true;
      }

      if (Math.abs(dx) > Math.abs(dy)) {
        e.preventDefault();
        scroller.scrollLeft = startLeft - dx;
      }
    };

    const onTouchEnd = () => {
      active = false;
      locked = false;
    };

    scroller.addEventListener("touchstart", onTouchStart, { passive: true });
    scroller.addEventListener("touchmove", onTouchMove, { passive: false });
    scroller.addEventListener("touchend", onTouchEnd, { passive: true });
    scroller.addEventListener("touchcancel", onTouchEnd, { passive: true });

    return () => {
      scroller.removeEventListener("touchstart", onTouchStart);
      scroller.removeEventListener("touchmove", onTouchMove);
      scroller.removeEventListener("touchend", onTouchEnd);
      scroller.removeEventListener("touchcancel", onTouchEnd);
    };
  }, []);

  /* ------------------------------------------------------------------------ */
  /* Column widths                                                             */
  /* ------------------------------------------------------------------------ */

  const [colWidths, setColWidths] = useState(() => loadWidths());

  useEffect(() => {
    saveWidths(colWidths);
  }, [colWidths]);

  const resizeInfoRef = useRef(null);
  const displayVouchersForSizingRef = useRef([]);

  const onResizeMove = useCallback((ev) => {
    const info = resizeInfoRef.current;
    if (!info) return;

    const delta = ev.clientX - info.startX;
    const nextWidth = Math.max(MIN_COL_W, info.startWidth + delta);

    setColWidths((w) => ({ ...w, [info.key]: nextWidth }));
  }, []);

  const endResize = useCallback(() => {
    resizeInfoRef.current = null;
    window.removeEventListener("mousemove", onResizeMove);
    window.removeEventListener("mouseup", endResize);
  }, [onResizeMove]);

  const startResize = useCallback(
    (e, key) => {
      e.preventDefault();
      e.stopPropagation();

      const th = e.currentTarget.closest("th");
      const rect = th.getBoundingClientRect();

      resizeInfoRef.current = {
        key,
        startX: e.clientX,
        startWidth: rect.width,
      };

      window.addEventListener("mousemove", onResizeMove);
      window.addEventListener("mouseup", endResize);
    },
    [onResizeMove, endResize]
  );

  useEffect(() => {
    return () => {
      window.removeEventListener("mousemove", onResizeMove);
      window.removeEventListener("mouseup", endResize);
    };
  }, [onResizeMove, endResize]);

  const autoFitColumn = useCallback((key) => {
    const headers = {
      voucherNo: t("vouchers:overview.voucherNoLabel"),
      voucherSearchNo: t("vouchers:overview.voucherSearchNoLabel"),
      category: t("vouchers:overview.categoryLabel").replace(":", ""),
      type: t("vouchers:overview.typeLabel"),
      issuer: t("vouchers:overview.issuerLabel"),
      value: t("vouchers:overview.valueTotal"),
      remaining: t("vouchers:overview.remainingTotal"),
      created: t("vouchers:overview.createdAtLabel"),
      expires: t("vouchers:overview.expirationDateLabel"),
      status: t("vouchers:overview.statusLabel").replace(":", ""),
      toNote: t("vouchers:common.markToNote"),
      comment: t("common:common.comment"),
      edit: t("vouchers:overview.columns.edit"),
      redeem: t("vouchers:overview.columns.redeem"),
      print: t("vouchers:overview.columns.print"),
      history: t("vouchers:overview.columns.history"),
      moreActions: t("vouchers:overview.columns.moreActions"),
    };

    const width = estimateColumnWidth(
      key,
      headers[key] || key,
      displayVouchersForSizingRef.current,
      tableRef.current
    );

    setColWidths((w) => ({ ...w, [key]: width }));
  }, [t]);

  const autoFitAll = useCallback(() => {
    const headers = {
      voucherNo: t("vouchers:overview.voucherNoLabel"),
      voucherSearchNo: t("vouchers:overview.voucherSearchNoLabel"),
      category: t("vouchers:overview.categoryLabel").replace(":", ""),
      type: t("vouchers:overview.typeLabel"),
      issuer: t("vouchers:overview.issuerLabel"),
      value: t("vouchers:overview.valueTotal"),
      remaining: t("vouchers:overview.remainingTotal"),
      created: t("vouchers:overview.createdAtLabel"),
      expires: t("vouchers:overview.expirationDateLabel"),
      status: t("vouchers:overview.statusLabel").replace(":", ""),
      toNote: t("vouchers:common.markToNote"),
      comment: t("common:common.comment"),
      edit: t("vouchers:overview.columns.edit"),
      redeem: t("vouchers:overview.columns.redeem"),
      print: t("vouchers:overview.columns.print"),
      history: t("vouchers:overview.columns.history"),
      moreActions: t("vouchers:overview.columns.moreActions"),
    };

    const rows = displayVouchersForSizingRef.current;
    const next = {};

    for (const key of COL_KEYS) {
      next[key] = estimateColumnWidth(
        key,
        headers[key] || key,
        rows,
        tableRef.current
      );
    }

    setColWidths(next);
  }, [t]);

  const resetAllWidths = useCallback(() => {
    localStorage.removeItem(WIDTHS_STORAGE_KEY);
    setColWidths({});
  }, []);

  const widthFor = useCallback(
    (key) => {
      const v = colWidths[key];
      return typeof v === "number" && v > 0 ? `${v}px` : undefined;
    },
    [colWidths]
  );

  const widthAttrFor = useCallback(
    (key) => {
      const v = colWidths[key];
      return typeof v === "number" && v > 0 ? v : undefined;
    },
    [colWidths]
  );

  const cellWidthStyle = useCallback(
    (key) => {
      const px = widthFor(key);
      return px ? { width: px, maxWidth: px } : undefined;
    },
    [widthFor]
  );

  /* ------------------------------------------------------------------------ */
  /* Issuer lookup                                                             */
  /* ------------------------------------------------------------------------ */

  const issuerMap = useMemo(() => {
    const m = new Map();

    users.forEach((u) => {
      m.set(
        u.id,
        `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim() || u.username
      );
    });

    return m;
  }, [users]);

  const getIssuerName = useCallback(
    (id) => issuerMap.get(id) ?? t("vouchers:common.unknown"),
    [issuerMap, t]
  );

  const statusChip = useMemo(() => {
    if (!selectedStatuses.length) return t("vouchers:overview.statusNone");
    if (selectedStatuses.length === ALL_STATUSES.length) return t("vouchers:overview.statusAll");
    const statuses = selectedStatuses
      .map((s) => t(`vouchers:statuses.${s}`, { defaultValue: s }))
      .join(", ");
    return t("vouchers:overview.statusSome", { statuses });
  }, [selectedStatuses, t]);

  /**
   * Localized table headers for the voucher/package overview grid.
   *
   * The column keys stay stable because they are used for persisted column
   * widths and sizing logic. Only labels are translated. This preserves the
   * existing localStorage contract while making the backoffice table bilingual.
   */
  const overviewTableColumns = useMemo(
    () => [
      { key: "voucherNo", label: t("vouchers:overview.columns.voucherNo") },
      {
        key: "voucherSearchNo",
        label: t("vouchers:overview.columns.voucherSearchNo"),
      },
      { key: "category", label: t("vouchers:overview.columns.category") },
      { key: "type", label: t("vouchers:overview.columns.type") },
      { key: "issuer", label: t("vouchers:overview.columns.issuer") },
      { key: "value", label: t("vouchers:overview.columns.value") },
      { key: "remaining", label: t("vouchers:overview.columns.remaining") },
      { key: "created", label: t("vouchers:overview.columns.created") },
      { key: "expires", label: t("vouchers:overview.columns.expires") },
      { key: "status", label: t("vouchers:overview.columns.status") },
      { key: "toNote", label: t("vouchers:overview.columns.toNote") },
      { key: "comment", label: t("vouchers:overview.columns.comment") },
      { key: "edit", label: t("vouchers:overview.columns.edit") },
      { key: "redeem", label: t("vouchers:overview.columns.redeem") },
      { key: "print", label: t("vouchers:overview.columns.print") },
      { key: "history", label: t("vouchers:overview.columns.history") },
      { key: "moreActions", label: t("vouchers:overview.columns.moreActions") },
    ],
    [t]
  );

  /* ------------------------------------------------------------------------ */
  /* Fetching                                                                  */
  /* ------------------------------------------------------------------------ */

  const usersAbortRef = useRef(null);
  const vouchersAbortRef = useRef(null);
  const qrAbortRef = useRef(null);

  const loadUsers = useCallback(async () => {
    usersAbortRef.current?.abort?.();
    const controller = new AbortController();
    usersAbortRef.current = controller;

    try {
      const data = await fetchUsers({ signal: controller.signal });
      if (controller.signal.aborted) return;
      setUsers(Array.isArray(data) ? data : []);
    } catch (e) {
      if (isRequestCanceled(e) || controller.signal.aborted) return;
      console.error("Load users error:", e);
      setErrorMsg(t("vouchers:overview.usersLoadError"));
    }
  }, [t]);

  const computeEffectiveFilters = useCallback(
    (filtersObj) => {
      const f = { ...filtersObj };

      if (!f.userIds?.length) delete f.userIds;
      if (!f.type) delete f.type;
      if (!f.voucherNo) delete f.voucherNo;
      if (!f.voucherSearchNo) delete f.voucherSearchNo;
      if (!f.createdAtFrom) delete f.createdAtFrom;
      if (!f.createdAtTo) delete f.createdAtTo;
      if (!f.expirationDateFrom) delete f.expirationDateFrom;
      if (!f.expirationDateTo) delete f.expirationDateTo;
      if (!f.redeemCreatedAtFrom) delete f.redeemCreatedAtFrom;
      if (!f.redeemCreatedAtTo) delete f.redeemCreatedAtTo;

      // QR mode uses a dedicated exact lookup. QR is not sent in normal mode.
      delete f.qrPayload;

      if (selectedStatuses.length) f.status = [...selectedStatuses];

      return f;
    },
    [selectedStatuses]
  );

  const loadVouchersNormal = useCallback(
    async (filtersObj) => {
      if (!selectedStatuses.length) {
        vouchersAbortRef.current?.abort?.();
        setLoading(false);
        setErrorMsg("");
        setServerVouchers([]);
        return;
      }

      vouchersAbortRef.current?.abort?.();
      const controller = new AbortController();
      vouchersAbortRef.current = controller;

      setLoading(true);
      setErrorMsg("");

      try {
        const effective = computeEffectiveFilters(filtersObj);
        const data = await fetchVouchers(effective, { signal: controller.signal });

        if (controller.signal.aborted) return;
        setServerVouchers(Array.isArray(data) ? data : []);
      } catch (e) {
        if (isRequestCanceled(e) || controller.signal.aborted) return;
        console.error("Load vouchers error:", e);
        setErrorMsg(t("vouchers:overview.vouchersLoadError"));
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    },
    [computeEffectiveFilters, selectedStatuses.length, t]
  );

  const loadVouchersByQr = useCallback(async (qrPayload) => {
    const qr = String(qrPayload || "").trim();

    if (!qr) {
      setQrVouchers([]);
      setQrBannerMsg("");
      return;
    }

    qrAbortRef.current?.abort?.();
    const controller = new AbortController();
    qrAbortRef.current = controller;

    setQrLoading(true);
    setQrBannerMsg("");
    setErrorMsg("");

    try {
      const data = await fetchVouchers(
        { qrPayload: qr },
        { signal: controller.signal }
      );

      if (controller.signal.aborted) return;

      const list = Array.isArray(data) ? data : [];
      const exact = list.filter((v) => String(v.qr_payload ?? "") === qr);

      const seen = new Set();
      const deduped = exact.filter((v) => {
        const id = v?.id;
        if (id == null) return true;
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });

      setQrVouchers(deduped);

      if (!deduped.length) {
        setQrBannerMsg(t("vouchers:overview.qrNoVoucherFound"));
      }
    } catch (e) {
      if (isRequestCanceled(e) || controller.signal.aborted) return;
      console.error("QR lookup error:", e);
      setErrorMsg(t("vouchers:overview.qrLookupFailed"));
      setQrVouchers([]);
    } finally {
      if (!controller.signal.aborted) setQrLoading(false);
    }
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem("vo_filters_open");
    if (saved != null) setFiltersOpen(saved === "1");
  }, [t]);

  useEffect(() => {
    loadUsers();

    return () => {
      usersAbortRef.current?.abort?.();
      vouchersAbortRef.current?.abort?.();
      qrAbortRef.current?.abort?.();
    };
  }, [loadUsers]);

  const debouncedFilters = useDebouncedValue(filters, 500);
  const debouncedStatuses = useDebouncedValue(selectedStatuses, 250);

  useEffect(() => {
    if (isQrMode) return;

    const lastField = lastChangedFieldRef.current;
    if (!lastField) return;
    if (!IMMEDIATE_FETCH_FIELDS.has(lastField)) return;

    lastChangedFieldRef.current = null;
    skipNextDebouncedFetchRef.current = true;

    loadVouchersNormal(filters);
  }, [filters, isQrMode, loadVouchersNormal]);

  useEffect(() => {
    if (isQrMode) return;

    if (skipNextDebouncedFetchRef.current) {
      skipNextDebouncedFetchRef.current = false;
      return;
    }

    loadVouchersNormal(debouncedFilters);
  }, [isQrMode, loadVouchersNormal, debouncedFilters, debouncedStatuses]);

  useEffect(() => {
    if (!isQrMode) return;
    loadVouchersByQr(filters.qrPayload);
  }, [isQrMode, filters.qrPayload, loadVouchersByQr]);

  /* ------------------------------------------------------------------------ */
  /* Display list + shared pagination                                          */
  /* ------------------------------------------------------------------------ */

  const displayVouchers = useMemo(() => {
    if (isQrMode) return qrVouchers;
    if (!selectedStatuses.length) return [];
    return serverVouchers;
  }, [isQrMode, qrVouchers, serverVouchers, selectedStatuses.length]);

  useEffect(() => {
    displayVouchersForSizingRef.current = displayVouchers;
  }, [displayVouchers]);

  const {
    page,
    pageSize,
    pageCount: totalPages,
    totalRows,
    paginatedItems: paginated,
    setPage,
    setPageSize,
    goPrev,
    goNext,
  } = useClientPagination(displayVouchers, {
    initialPageSize,
  });

  /* ------------------------------------------------------------------------ */
  /* URL synchronization                                                        */
  /* ------------------------------------------------------------------------ */

  /**
   * Restore initial page from URL after useClientPagination has created its state.
   *
   * This effect runs once.
   *
   * Why not pass initialPage directly to the hook?
   * - the current shared hook contract is intentionally stable,
   * - we do not want to modify useClientPagination just for this page.
   */
  const didRestoreInitialPageRef = useRef(false);

  useEffect(() => {
    if (didRestoreInitialPageRef.current) return;

    didRestoreInitialPageRef.current = true;

    if (initialPageFromUrl > 1) {
      setPage(initialPageFromUrl);
    }
  }, [initialPageFromUrl, setPage]);

  /**
   * Keep the browser URL aligned with the current overview state.
   *
   * This is the key part of preserving the user’s working context.
   *
   * Example:
   * - user enters filter voucherNo = K/25/12/04,
   * - URL becomes /vouchers/overview?voucherNo=K%2F25%2F12%2F04&pageSize=50,
   * - user opens a voucher,
   * - VoucherPage receives returnTo with the full URL,
   * - Back to overview returns to the same filtered list.
   *
   * replace: true is important:
   * - typing in filters should not create dozens of browser-history entries,
   * - browser Back should still behave naturally.
   */
  useEffect(() => {
    const params = buildVoucherOverviewSearchParams({
      filters,
      selectedStatuses,
      page,
      pageSize,
    });

    const nextSearch = params.toString();
    const currentSearch = String(location.search || "").replace(/^\?/, "");

    if (nextSearch === currentSearch) {
      return;
    }

    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true }
    );
  }, [
    filters,
    selectedStatuses,
    page,
    pageSize,
    location.pathname,
    location.search,
    navigate,
  ]);
  
  const handlePageSizeChange = useCallback(
    (nextPageSize) => {
      setPageSize(nextPageSize);

      try {
        localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(nextPageSize));
      } catch {
        // Ignore storage errors.
      }
    },
    [setPageSize]
  );

  const { totalValue, totalRemaining } = useMemo(() => {
    const acc = { totalValue: 0, totalRemaining: 0 };

    for (const v of displayVouchers) {
      acc.totalValue += Number(v.value) || 0;
      acc.totalRemaining += Number(v.remaining_balance) || 0;
    }

    return acc;
  }, [displayVouchers]);

  const { pageValue, pageRemaining } = useMemo(() => {
    const acc = { pageValue: 0, pageRemaining: 0 };

    for (const v of paginated) {
      acc.pageValue += Number(v.value) || 0;
      acc.pageRemaining += Number(v.remaining_balance) || 0;
    }

    return acc;
  }, [paginated]);

  /* ------------------------------------------------------------------------ */
  /* Filter handlers                                                           */
  /* ------------------------------------------------------------------------ */

  const handleFilterChange = useCallback(
    (e) => {
      const { name, value, multiple, selectedOptions } = e.target;

      setPage(1);
      lastChangedFieldRef.current = name;

      if (multiple) {
        const arr = Array.from(selectedOptions).map((o) => o.value);
        setFilters((f) => ({ ...f, [name]: arr }));
      } else {
        setFilters((f) => ({ ...f, [name]: value }));
      }
    },
    [setPage]
  );

  const resetFilters = useCallback(() => {
    setPage(1);

    setFilters({
      ...DEFAULT_VOUCHER_OVERVIEW_FILTERS,
    });

    setSelectedStatuses([...DEFAULT_VOUCHER_OVERVIEW_STATUSES]);

    setQrVouchers([]);
    setQrBannerMsg("");
    setErrorMsg("");
  }, [setPage]);

  const toggleFiltersOpen = useCallback(() => {
    setFiltersOpen((v) => {
      const next = !v;
      localStorage.setItem("vo_filters_open", next ? "1" : "0");
      return next;
    });
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (
        e.key === "/" &&
        !e.ctrlKey &&
        !e.altKey &&
        !e.metaKey &&
        !/^(INPUT|SELECT|TEXTAREA)$/.test(document.activeElement?.tagName || "")
      ) {
        e.preventDefault();
        quickSearchRef.current?.focus?.();
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!filtersOpen) return;

    const t = setTimeout(() => {
      qrInputRef.current?.focus?.();
      try {
        qrInputRef.current?.select?.();
      } catch {
        // Ignore selection errors.
      }
    }, 0);

    return () => clearTimeout(t);
  }, [filtersOpen]);

  /* ------------------------------------------------------------------------ */
  /* Scanner catcher                                                           */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    const MIN_LEN_TO_COMMIT = 12;
    const MAX_GAP_MS = 35;
    const HARD_RESET_MS = 250;
    const INACTIVITY_COMMIT_MS = 80;
    const MAX_TOTAL_MS = 900;

    let buf = "";
    let startedAt = 0;
    let lastAt = 0;
    let maxGap = 0;
    let inactivityTimer = 0;

    const clearInactivity = () => {
      if (inactivityTimer) window.clearTimeout(inactivityTimer);
      inactivityTimer = 0;
    };

    const reset = () => {
      clearInactivity();
      buf = "";
      startedAt = 0;
      lastAt = 0;
      maxGap = 0;
      scanActiveRef.current = false;
    };

    const shouldTreatAsScan = () => {
      if (buf.length < MIN_LEN_TO_COMMIT) return false;
      if (maxGap > MAX_GAP_MS) return false;

      const total = Date.now() - startedAt;
      if (total > MAX_TOTAL_MS) return false;

      return true;
    };

    const commitToQr = (payload) => {
      const qr = String(payload || "").trim();
      if (!qr) return;

      setPage(1);
      lastChangedFieldRef.current = "qrPayload";
      setFilters((f) => ({ ...f, qrPayload: qr }));

      requestAnimationFrame(() => {
        qrInputRef.current?.focus?.();
        try {
          qrInputRef.current?.select?.();
        } catch {
          // Ignore selection errors.
        }
      });
    };

    const onKeyDownCapture = (e) => {
      if (e.ctrlKey || e.altKey || e.metaKey) return;

      const now = Date.now();

      if (e.key === "Enter") {
        if (buf.length && (scanActiveRef.current || shouldTreatAsScan())) {
          e.preventDefault();
          e.stopPropagation();
          commitToQr(buf);
        }
        reset();
        return;
      }

      if (!e.key || e.key.length !== 1) return;

      if (lastAt && now - lastAt > HARD_RESET_MS) {
        reset();
      }

      if (!startedAt) startedAt = now;
      if (lastAt) maxGap = Math.max(maxGap, now - lastAt);
      lastAt = now;
      buf += e.key;

      if (!scanActiveRef.current && shouldTreatAsScan()) {
        scanActiveRef.current = true;
      }

      if (scanActiveRef.current) {
        e.preventDefault();
        e.stopPropagation();
      }

      clearInactivity();
      inactivityTimer = window.setTimeout(() => {
        if (scanActiveRef.current && buf.length) {
          commitToQr(buf);
        }
        reset();
      }, INACTIVITY_COMMIT_MS);
    };

    const onBeforeInputCapture = (e) => {
      if (!scanActiveRef.current) return;
      e.preventDefault();
      e.stopPropagation();
    };

    window.addEventListener("keydown", onKeyDownCapture, true);
    window.addEventListener("beforeinput", onBeforeInputCapture, true);

    return () => {
      window.removeEventListener("keydown", onKeyDownCapture, true);
      window.removeEventListener("beforeinput", onBeforeInputCapture, true);
      reset();
    };
  }, [setPage]);

  const handleQrFocus = useCallback(() => {
    try {
      qrInputRef.current?.select?.();
    } catch {
      // Ignore selection errors.
    }

    requestAnimationFrame(() => {
      try {
        qrInputRef.current?.select?.();
      } catch {
        // Ignore selection errors.
      }
    });
  }, []);

  const handleQrKeyDown = useCallback(
    (e) => {
      if (e.key !== "Enter") return;

      e.preventDefault();
      const next = String(e.currentTarget.value || "").trim();

      setPage(1);
      lastChangedFieldRef.current = "qrPayload";
      setFilters((f) => ({ ...f, qrPayload: next }));
    },
    [setPage]
  );

  const focusQrForScan = useCallback(() => {
    requestAnimationFrame(() => {
      qrInputRef.current?.focus?.();
      try {
        qrInputRef.current?.select?.();
      } catch {
        // Ignore selection errors.
      }
    });
  }, []);

  const exitQrMode = useCallback(() => {
    setPage(1);
    lastChangedFieldRef.current = "qrPayload";
    setFilters((f) => ({ ...f, qrPayload: "" }));
    setQrVouchers([]);
    setQrBannerMsg("");
    setErrorMsg("");
    focusQrForScan();
  }, [focusQrForScan, setPage]);

  const activeFilterChips = useMemo(() => {
    const chips = [];

    if (isQrMode) {
      chips.push({
        key: "qr",
        label: t("vouchers:overview.filterChipQr", {
          value: String(filters.qrPayload || "").trim(),
        }),
        kind: "qr",
        onClear: exitQrMode,
      });
      return chips;
    }

    if (isNonEmpty(filters.voucherNo)) {
      chips.push({
        key: "voucherNo",
        label: t("vouchers:overview.filterChipVoucherNo", {
          value: filters.voucherNo.trim(),
        }),
        onClear: () => setFilters((f) => ({ ...f, voucherNo: "" })),
      });
    }

    if (isNonEmpty(filters.voucherSearchNo)) {
      chips.push({
        key: "voucherSearchNo",
        label: t("vouchers:overview.filterChipVoucherSearchNo", {
          value: filters.voucherSearchNo.trim(),
        }),
        onClear: () => setFilters((f) => ({ ...f, voucherSearchNo: "" })),
      });
    }

    if (isNonEmpty(filters.createdAtFrom) || isNonEmpty(filters.createdAtTo)) {
      chips.push({
        key: "created",
        label: t("vouchers:overview.filterChipCreated", {
          from: formatDateChip(filters.createdAtFrom) || "…",
          to: formatDateChip(filters.createdAtTo) || "…",
        }),
        onClear: () =>
          setFilters((f) => ({ ...f, createdAtFrom: "", createdAtTo: "" })),
      });
    }

    if (
      isNonEmpty(filters.expirationDateFrom) ||
      isNonEmpty(filters.expirationDateTo)
    ) {
      chips.push({
        key: "expiration",
        label: t("vouchers:overview.filterChipExpires", {
          from: formatDateChip(filters.expirationDateFrom) || "…",
          to: formatDateChip(filters.expirationDateTo) || "…",
        }),
        onClear: () =>
          setFilters((f) => ({
            ...f,
            expirationDateFrom: "",
            expirationDateTo: "",
          })),
      });
    }

    if (
      isNonEmpty(filters.redeemCreatedAtFrom) ||
      isNonEmpty(filters.redeemCreatedAtTo)
    ) {
      chips.push({
        key: "redeem",
        label: t("vouchers:overview.filterChipRedeemExists", {
          from: formatDateChip(filters.redeemCreatedAtFrom) || "…",
          to: formatDateChip(filters.redeemCreatedAtTo) || "…",
        }),
        onClear: () =>
          setFilters((f) => ({
            ...f,
            redeemCreatedAtFrom: "",
            redeemCreatedAtTo: "",
          })),
      });
    }

    if (isNonEmpty(filters.type)) {
      chips.push({
        key: "type",
        label: t("vouchers:overview.filterChipType", {
          value: t(`vouchers:types.${filters.type}`, { defaultValue: filters.type }),
        }),
        onClear: () => setFilters((f) => ({ ...f, type: "" })),
      });
    }

    if (Array.isArray(filters.userIds) && filters.userIds.length) {
      const names = filters.userIds
        .map((id) => getIssuerName(Number(id) || id))
        .filter(Boolean);

      const pretty =
        names.length <= 2
          ? names.join(", ")
          : `${names.slice(0, 2).join(", ")} +${names.length - 2}`;

      chips.push({
        key: "issuer",
        label: t("vouchers:overview.filterChipIssuer", { value: pretty }),
        onClear: () => setFilters((f) => ({ ...f, userIds: [] })),
      });
    }

    const isDefaultActiveOnly =
      selectedStatuses.length === 1 && selectedStatuses[0] === "Active";

    if (!isDefaultActiveOnly) {
      const label =
        selectedStatuses.length === 0
          ? t("vouchers:overview.filterChipStatusNone")
          : selectedStatuses.length === ALL_STATUSES.length
          ? t("vouchers:overview.filterChipStatusAll")
          : t("vouchers:overview.filterChipStatusSome", {
              value: selectedStatuses
                .map((status) =>
                  t(`vouchers:statuses.${status}`, { defaultValue: status })
                )
                .join(", "),
            });

      chips.push({
        key: "status",
        label,
        onClear: () => setSelectedStatuses(["Active"]),
      });
    }

    return chips;
  }, [filters, isQrMode, selectedStatuses, exitQrMode, getIssuerName, t]);

  /**
   * Compact mobile summary shown when the filter drawer is collapsed.
   *
   * The desktop toolbar intentionally keeps the existing, explicit controls. On
   * small touch screens, however, the filters are supporting controls and must
   * not consume the initial viewport. This summary keeps the important context
   * visible while allowing the voucher rows to start much higher on the screen.
   */
  const mobileFilterSummary = useMemo(() => {
    const count = activeFilterChips.length;

    if (!count) return statusChip;

    return t("vouchers:overview.mobileFilterSummary", {
      status: statusChip,
      count,
      defaultValue: `${statusChip} · ${count} active`,
    });
  }, [activeFilterChips.length, statusChip, t]);

  /* ------------------------------------------------------------------------ */
  /* Redeem history prefetch                                                   */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    let cancelled = false;

    async function prefetchCounts() {
      const toFetch = paginated.filter((v) => historyCount[v.id] == null);

      for (const v of toFetch) {
        try {
          const data = await fetchRedeemHistory(v.id);
          if (cancelled) return;

          const items = Array.isArray(data) ? data : data?.items ?? [];

          setHistoryCount((m) => ({ ...m, [v.id]: items.length }));

          if (items.length > 0) {
            setRedeemHistory((m) => ({ ...m, [v.id]: items }));
          }
        } catch (e) {
          if (cancelled) return;

          if (isEmptyHistoryError(e)) {
            setHistoryCount((m) => ({ ...m, [v.id]: 0 }));
          } else {
            console.error("Prefetch history count failed for", v.id, e);
          }
        }
      }
    }

    if (paginated.length) prefetchCounts();

    return () => {
      cancelled = true;
    };
  }, [paginated, historyCount]);

  /* ------------------------------------------------------------------------ */
  /* Return-to navigation                                                       */
  /* ------------------------------------------------------------------------ */
  /**
   * Detail/action pages should not guess where to return.
   *
   * VoucherOverview is the owner of its current working context, so it passes
   * that context explicitly through React Router location.state.returnTo.
   *
   * Consumers:
   * - VoucherPage
   * - RedeemVoucherPage
   * - RedeemEditPage
   */

  /**
   * Build a stable return target for edit/detail/action pages.
   *
   * This preserves the exact current overview state:
   * - normal filters,
   * - status selection,
   * - current page,
   * - page size,
   * - QR mode if active.
   *
   * Important implementation detail:
   * We do NOT read location.search here.
   *
   * Instead, we build the return path from the current React state so that even
   * very recent changes are included when the user immediately clicks an action.
   */
  const buildReturnToPath = useCallback(() => {
    return buildVoucherOverviewReturnToPath({
      pathname: location.pathname,
      filters,
      selectedStatuses,
      page,
      pageSize,
    });
  }, [
    location.pathname,
    filters,
    selectedStatuses,
    page,
    pageSize,
  ]);

  /* ------------------------------------------------------------------------ */
  /* Row actions                                                               */
  /* ------------------------------------------------------------------------ */

  const handleEdit = useCallback(
    (voucher) => {
      navigate(`/vouchers/${voucher.id}`, {
        state: {
          voucher,
          phase: "edit",

          /**
           * VoucherPage uses this value for "Back to overview".
           *
           * The value includes the current VoucherOverview working context:
           * - filter fields,
           * - statuses,
           * - page/pageSize,
           * - QR mode if active.
           */
          returnTo: buildReturnToPath(),
        },
      });
    },
    [navigate, buildReturnToPath]
  );

  const handleArchive = useCallback(
    async (id) => {
      if (archivingId) return;
      setArchivingId(id);

      try {
        await archiveVoucher(id);
        setExpandedMoreActionsVoucherId(null);

        if (isQrMode) {
          await loadVouchersByQr(filters.qrPayload);
        } else {
          await loadVouchersNormal(debouncedFilters);
        }
      } catch (e) {
        console.error("Archive error:", e);
        setErrorMsg(t("vouchers:overview.archiveFailed"));
      } finally {
        setArchivingId(null);
      }
    },
    [
      archivingId,
      isQrMode,
      loadVouchersByQr,
      filters.qrPayload,
      loadVouchersNormal,
      debouncedFilters,
      t,
    ]
  );

  const handleUnarchive = useCallback(
    async (id) => {
      if (unarchivingId) return;
      setUnarchivingId(id);

      try {
        await unarchiveVoucher(id);
        setExpandedMoreActionsVoucherId(null);

        if (isQrMode) {
          await loadVouchersByQr(filters.qrPayload);
        } else {
          await loadVouchersNormal(debouncedFilters);
        }
      } catch (e) {
        console.error("Unarchive error:", e);
        setErrorMsg(t("vouchers:overview.unarchiveFailed"));
      } finally {
        setUnarchivingId(null);
      }
    },
    [
      unarchivingId,
      isQrMode,
      loadVouchersByQr,
      filters.qrPayload,
      loadVouchersNormal,
      debouncedFilters,
      t,
    ]
  );

  const closeEmailDialog = useCallback(() => {
    setEmailDialogOpen(false);
    setEmailVoucher(null);
    setEmailTo("");
    setEmailCc("");
    setEmailAttachPdf(true);
    setEmailDefaultsLoading(false);
  }, []);

  /**
   * Open the manual e-mail dialog for a single voucher/package row.
   *
   * Important design rule:
   * - the dialog opens immediately so the user sees a responsive UI,
   * - online-order recipient defaults are then loaded lazily from the backend,
   * - the operator can still overwrite both Email and CC before sending.
   */
  const handleOpenEmailDialog = useCallback(
    async (voucher) => {
      if (!voucher) return;

      setErrorMsg("");
      setSuccessMsg("");
      setExpandedMoreActionsVoucherId(null);
      setEmailVoucher(voucher);
      setEmailTo("");
      setEmailCc("");
      setEmailAttachPdf(true);
      setEmailDialogOpen(true);
      setEmailDefaultsLoading(true);

      try {
        const defaults = await getVoucherEmailDefaults(voucher.id);
        setEmailTo(defaults?.to || "");
        setEmailCc(defaults?.cc || "");
      } catch (e) {
        // Missing defaults are not fatal. Manual vouchers often do not have an
        // online order, so the user can simply enter the e-mail address.
        console.warn("Voucher e-mail defaults could not be loaded:", e);
      } finally {
        setEmailDefaultsLoading(false);
      }
    },
    []
  );

  /**
   * Submit manual voucher/package e-mail sending.
   *
   * The backend performs the authoritative validation and SMTP/PDF work. The
   * frontend validation below is only a small UX guard to avoid clearly empty
   * submissions.
   */
  const handleSendVoucherEmail = useCallback(async () => {
    if (!emailVoucher || sendingEmailId) return;

    const to = String(emailTo || "").trim();
    const cc = String(emailCc || "").trim();

    if (!to) {
      setErrorMsg(t("vouchers:overview.emailToRequired"));
      return;
    }

    setSendingEmailId(emailVoucher.id);
    setErrorMsg("");
    setSuccessMsg("");

    try {
      await sendVoucherEmail(emailVoucher.id, {
        to,
        cc,
        attachPdf: !!emailAttachPdf,
      });

      setSuccessMsg(t("vouchers:overview.emailSentSuccess"));
      closeEmailDialog();
    } catch (e) {
      console.error("Voucher e-mail send error:", e);
      setErrorMsg(
        e?.response?.data?.message || t("vouchers:overview.emailSendFailed")
      );
    } finally {
      setSendingEmailId(null);
    }
  }, [
    emailVoucher,
    sendingEmailId,
    emailTo,
    emailCc,
    emailAttachPdf,
    closeEmailDialog,
    t,
  ]);

  /**
   * Expand or collapse the secondary action row for a single voucher.
   *
   * Important UX rule:
   * - fast, frequently used actions stay as dedicated table columns,
   * - rare administrative actions are shown only on demand, similarly to the
   *   existing redeem-history expansion pattern.
   */
  const handleMoreActionsToggle = useCallback((voucherId) => {
    setExpandedMoreActionsVoucherId((current) =>
      current === voucherId ? null : voucherId
    );
  }, []);

  const handleRedeem = useCallback(
    (voucher) => {
      const statusText = voucher.status ?? (voucher.open ? "Active" : "Archived");
      const canRedeem = statusText === "Active" && voucher.open === true;
      if (!canRedeem) return;

      navigate("/vouchers/redeem", {
        state: {
          voucher,

          /**
           * RedeemVoucherPage should return to the exact overview state after
           * successful redeem or when the operator clicks Cancel.
           */
          returnTo: buildReturnToPath(),
        },
      });
    },
    [navigate, buildReturnToPath]
  );

  const handleEditRedeem = useCallback(
    (entry, voucher) => {
      navigate(`/vouchers/redeems/${entry.id}/edit`, {
        state: {
          voucherId: voucher.id,

          /**
           * RedeemEditPage should use this path after Save and Cancel.
           *
           * This avoids the annoying UX where the user edits one redeem entry and
           * then lands on a clean VoucherOverview without the previous filters.
           */
          returnTo: buildReturnToPath(),
        },
      });
    },
    [navigate, buildReturnToPath]
  );

  const handleRedeemHistoryToggle = useCallback(
    async (voucherId) => {
      if (expandedVoucherId === voucherId) {
        setExpandedVoucherId(null);
        return;
      }

      if (redeemHistory[voucherId]) {
        setExpandedVoucherId(voucherId);
        return;
      }

      setExpandedVoucherId(voucherId);

      try {
        const data = await fetchRedeemHistory(voucherId);
        const items = Array.isArray(data) ? data : data?.items ?? [];

        setHistoryCount((m) => ({ ...m, [voucherId]: items.length }));

        if (items.length === 0) {
          setRedeemHistory((m) => ({ ...m, [voucherId]: [] }));
          setExpandedVoucherId(null);
          return;
        }

        setRedeemHistory((m) => ({ ...m, [voucherId]: items }));
      } catch (e) {
        if (isEmptyHistoryError(e)) {
          setHistoryCount((m) => ({ ...m, [voucherId]: 0 }));
          setRedeemHistory((m) => ({ ...m, [voucherId]: [] }));
          setExpandedVoucherId(null);
          return;
        }

        console.error("Redeem history error:", e);
      }
    },
    [expandedVoucherId, redeemHistory]
  );

  const handleToggleToNote = useCallback(
    async (voucher, nextChecked) => {
      if (updatingToNoteId) return;

      const count =
        historyCount[voucher.id] != null
          ? historyCount[voucher.id]
          : redeemHistory[voucher.id]?.length ?? 0;

      let propagate = false;
      if (count > 0) {
        propagate = window.confirm(
          t("vouchers:overview.propagateToNoteConfirm")
        );
      }

      setUpdatingToNoteId(voucher.id);
      setErrorMsg("");

      try {
        await updateVoucherToNote(voucher.id, !!nextChecked, { propagate });

        setServerVouchers((list) =>
          list.map((v) =>
            v.id === voucher.id ? { ...v, to_note: !!nextChecked } : v
          )
        );

        setQrVouchers((list) =>
          list.map((v) =>
            v.id === voucher.id ? { ...v, to_note: !!nextChecked } : v
          )
        );

        if (propagate && redeemHistory[voucher.id]) {
          setRedeemHistory((m) => ({
            ...m,
            [voucher.id]: (m[voucher.id] || []).map((e) => ({
              ...e,
              noted: !!nextChecked,
            })),
          }));
        }
      } catch (e) {
        console.error("Update to_note failed:", e);
        setErrorMsg(
          e?.response?.data?.message ||
            t("vouchers:overview.updateToNoteFailed")
        );
      } finally {
        setUpdatingToNoteId(null);
      }
    },
    [historyCount, redeemHistory, updatingToNoteId]
  );

  /* ------------------------------------------------------------------------ */
  /* Preview / print                                                           */
  /* ------------------------------------------------------------------------ */

  const handleOpenPrintPreview = useCallback((voucher) => {
    const mappedPreview = mapVoucherRowToPreview(voucher) || {};

    const safePreview = {
      businessMode:
        mappedPreview.businessMode || getBusinessModeFromVoucherRow(voucher),
      ...mappedPreview,
    };

    setPreviewVoucherRow(voucher);
    setPreviewData(safePreview);
    setErrorMsg("");
    setPreviewOpen(true);
  }, []);

  const handleClosePrintPreview = useCallback(() => {
    setPreviewOpen(false);
    setPreviewVoucherRow(null);
    setPreviewData(null);
  }, []);

  useEffect(() => {
    if (!previewOpen) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        handleClosePrintPreview();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [previewOpen, handleClosePrintPreview]);

  const handlePrintPdf = useCallback(async () => {
    if (!previewVoucherRow) return;

    setPrinting(true);
    setErrorMsg("");

    try {
      const pdfData = await downloadVoucherPdf(previewVoucherRow.id);
      const pdfBlob =
        pdfData instanceof Blob
          ? pdfData
          : new Blob([pdfData], { type: "application/pdf" });

      const blobUrl = window.URL.createObjectURL(pdfBlob);
      window.open(blobUrl, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
    } catch (err) {
      console.error("Error while opening voucher PDF:", err);
      setErrorMsg(t("vouchers:overview.pdfOpenFailed"));
    } finally {
      setPrinting(false);
    }
  }, [previewVoucherRow, t]);

  /* ------------------------------------------------------------------------ */
  /* Status filter handlers                                                    */
  /* ------------------------------------------------------------------------ */

  const toggleStatus = useCallback(
    (status) => {
      setPage(1);
      setSelectedStatuses((prev) => {
        const has = prev.includes(status);
        return has ? prev.filter((s) => s !== status) : [...prev, status];
      });
    },
    [setPage]
  );

  const selectAllStatuses = useCallback(() => {
    setPage(1);
    setSelectedStatuses([...ALL_STATUSES]);
  }, [setPage]);

  const clearStatuses = useCallback(() => {
    setPage(1);
    setSelectedStatuses([]);
  }, [setPage]);

  const previewUiMeta = useMemo(() => {
    return getEntityUiMeta(previewVoucherRow, t);
  }, [previewVoucherRow, t]);

  const tipText = t("vouchers:overview.qrTip");
  const hintText = t("vouchers:overview.qrHint");

  /* ------------------------------------------------------------------------ */
  /* Render                                                                    */
  /* ------------------------------------------------------------------------ */

  return (
    <div
      className={`voucher-overview ${
        filtersOpen ? "is-filters-open" : "is-filters-collapsed"
      }`}
      role="main"
      aria-label={t("vouchers:overview.ariaLabel")}
    >
      {/* Toolbar */}
      <div className="vo-toolbar">
        <h1>{t("vouchers:overview.title")}</h1>

        <div
          className="vo-toolbar-actions"
          role="group"
          aria-label={t("vouchers:overview.toolbarActionsAriaLabel")}
        >
          {isQrMode && (
            <div
              className="vo-qr-chip"
              role="status"
              aria-label={t("vouchers:overview.qrModeActiveAriaLabel")}
            >
              <span className="vo-qr-chip-label">
                {t("vouchers:overview.qrModeLabel")}
                <strong className="vo-qr-chip-value">
                  {String(filters.qrPayload || "").trim()}
                </strong>
              </span>

              <button
                type="button"
                className="btn btn-secondary btn-xs"
                onClick={exitQrMode}
                title={t("vouchers:overview.exitQrModeTitle")}
              >
                {t("vouchers:overview.exitClearButton")}
              </button>

              {qrLoading && (
                <span className="vo-qr-chip-loading">{t("vouchers:overview.searching")}</span>
              )}
            </div>
          )}

          <div
            className={`vo-toolbar-status ${isQrMode ? "is-disabled" : ""}`}
            role="group"
            aria-label={t("vouchers:overview.statusFilterAriaLabel")}
            title={
              isQrMode
                ? t("vouchers:overview.statusFilterIgnoredTitle")
                : t("vouchers:overview.statusFilterTitle")
            }
          >
            <span className="vo-toolbar-status-label">{t("vouchers:overview.statusLabel")}</span>

            <div className="vo-toolbar-status-items">
              {ALL_STATUSES.map((s) => {
                const isDraft = s === "Draft";
                const isActive = s === "Active";
                const isCancelled = s === "Cancelled";
                const checked = selectedStatuses.includes(s);

                return (
                  <label
                    key={s}
                    className={[
                      "vo-status-item",
                      "vo-status-item--toolbar",
                      isDraft ? "vo-status-pill vo-status-pill--draft" : "",
                      checked && isActive
                        ? "vo-status-pill vo-status-pill--active is-checked"
                        : "",
                      checked && isCancelled
                        ? "vo-status-pill vo-status-pill--cancelled is-checked"
                        : "",
                      isQrMode ? "is-disabled" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleStatus(s)}
                      disabled={isQrMode}
                      aria-label={`${t("vouchers:overview.statusLabel")} ${t(`vouchers:statuses.${s}`, { defaultValue: s })}`}
                    />
                    <span className="vo-status-label">{t(`vouchers:statuses.${s}`, { defaultValue: s })}</span>
                  </label>
                );
              })}
            </div>

            <div className="vo-toolbar-status-actions">
              <button
                type="button"
                className="btn btn-ghost btn-xs"
                onClick={selectAllStatuses}
                disabled={isQrMode}
                title={t("vouchers:overview.selectAllStatusesTitle")}
              >
                {t("vouchers:overview.allStatusesButton")}
              </button>

              <button
                type="button"
                className="btn btn-ghost btn-xs"
                onClick={clearStatuses}
                disabled={isQrMode}
                title={t("vouchers:overview.clearStatusFilterTitle")}
              >
                {t("vouchers:overview.noneStatusesButton")}
              </button>
            </div>
          </div>

          <span
            className={`btn btn-ghost vo-status-chip ${isQrMode ? "is-muted" : ""}`}
            aria-label={t("vouchers:overview.currentStatusSelectionAriaLabel")}
            title={t("vouchers:overview.currentlySelectedStatusesTitle")}
            style={{ cursor: "default" }}
          >
            <span className="vo-status-chip-desktop-text">{statusChip}</span>
            <span className="vo-status-chip-mobile-text">{mobileFilterSummary}</span>
          </span>

          <button
            className="btn btn-secondary vo-filter-toggle-btn"
            type="button"
            onClick={toggleFiltersOpen}
            aria-expanded={filtersOpen}
            aria-controls="filters-drawer"
          >
            {filtersOpen ? t("vouchers:overview.hideFilters") : t("vouchers:overview.showFilters")}
          </button>

          <button className="btn btn-ghost vo-reset-filters-btn" onClick={resetFilters} title={t("vouchers:overview.resetFiltersTitle")}>
            {t("vouchers:overview.resetFilters")}
          </button>

          <button
            className="btn btn-secondary vo-column-tool-btn"
            type="button"
            onClick={autoFitAll}
            title={t("vouchers:overview.autoFitColumnsTitle")}
          >
            {t("vouchers:overview.autoFitColumns")}
          </button>

          <button
            className="btn btn-ghost vo-column-tool-btn"
            type="button"
            onClick={resetAllWidths}
            title={t("vouchers:overview.resetWidthsTitle")}
          >
            {t("vouchers:overview.resetWidths")}
          </button>
        </div>
      </div>

      {/* Error / success / QR banner */}
      {(errorMsg || qrBannerMsg) && (
        <div role="alert" className="vo-alert vo-alert--error">
          {errorMsg || qrBannerMsg}
        </div>
      )}

      {successMsg && !errorMsg && !qrBannerMsg && (
        <div role="status" className="vo-alert vo-alert--success">
          {successMsg}
        </div>
      )}

      {/* Filters drawer */}
      {filtersOpen && (
        <div
          className="vo-filters-drawer"
          id="filters-drawer"
          aria-label={t("vouchers:overview.filtersAriaLabel")}
        >
          <div className="vo-filters-grid">
            <div
              className={`vo-filter-group vo-filter-qr ${
                isQrMode ? "is-active" : ""
              }`}
            >
              <label htmlFor="qrPayload">{t("vouchers:overview.scanQrLabel")}</label>

              <input
                ref={qrInputRef}
                id="qrPayload"
                type="text"
                name="qrPayload"
                value={filters.qrPayload}
                onChange={handleFilterChange}
                onKeyDown={handleQrKeyDown}
                onFocus={handleQrFocus}
                onBeforeInput={(e) => {
                  if (scanActiveRef.current) e.preventDefault();
                }}
                placeholder={t("vouchers:overview.scanQrPlaceholder")}
                autoComplete="off"
                inputMode="search"
              />

              <div className="vo-inline-actions">
                <button
                  type="button"
                  className="btn btn-secondary btn-xs"
                  onClick={focusQrForScan}
                  title={t("vouchers:overview.focusQrTitle")}
                >
                  {t("vouchers:overview.readyToScanButton")}
                </button>

                <button
                  type="button"
                  className="btn btn-ghost btn-xs"
                  onClick={exitQrMode}
                  title={t("vouchers:overview.clearQrTitle")}
                  disabled={!isQrMode && !filters.qrPayload}
                >
                  {t("vouchers:overview.clearQrButton")}
                </button>
              </div>

              <div className="vo-qr-foot" aria-label={t("vouchers:overview.qrTipsAriaLabel")}>
                <p className="vo-micro-hint" title={tipText}>
                  {tipText}
                </p>
                <p className="vo-hint" title={hintText}>
                  {hintText}
                </p>
              </div>
            </div>

            <div
              className={`vo-filter-group vo-filter-vno ${
                isQrMode ? "is-frozen" : ""
              }`}
            >
              <label htmlFor="voucherNo">{t("vouchers:overview.voucherNoLabel")}</label>
              <input
                ref={quickSearchRef}
                id="voucherNo"
                type="text"
                name="voucherNo"
                value={filters.voucherNo}
                onChange={handleFilterChange}
                placeholder={t("vouchers:overview.voucherNoPlaceholder")}
                autoComplete="off"
                inputMode="search"
              />
              {/*
                The helper text is localized as well because employees use it as
                operational guidance when searching for a voucher number fragment.
                Keep the search behavior unchanged; only the displayed copy changes.
              */}
              <p className="vo-field-help is-tooltip">
                {t("vouchers:overview.partialSearchHelp")}
              </p>
            </div>

            <div
              className={`vo-filter-group vo-filter-vsno ${
                isQrMode ? "is-frozen" : ""
              }`}
            >
              <label htmlFor="voucherSearchNo">{t("vouchers:overview.voucherSearchNoLabel")}</label>
              <input
                id="voucherSearchNo"
                type="text"
                name="voucherSearchNo"
                value={filters.voucherSearchNo}
                onChange={handleFilterChange}
                placeholder={t("vouchers:overview.voucherSearchNoPlaceholder")}
                autoComplete="off"
              />
              {/*
                Same partial-search semantics as voucherNo. The text is separate
                because search number formatting may diverge from voucher number
                formatting later, while both keys can currently share the same value.
              */}
              <p className="vo-field-help is-tooltip">
                {t("vouchers:overview.voucherSearchNoPartialSearchHelp")}
              </p>
            </div>

            <div
              className={`vo-filter-group vo-filter-created ${
                isQrMode ? "is-frozen" : ""
              }`}
            >
              <div className="vo-filter-card vo-filter-card--date">
                <label>{t("vouchers:overview.createdAtLabel")}</label>
                <div className="vo-filter-row">
                  <input
                    type="date"
                    name="createdAtFrom"
                    value={filters.createdAtFrom}
                    onChange={handleFilterChange}
                    aria-label={t("vouchers:overview.createdFromAriaLabel")}
                  />
                  <input
                    type="date"
                    name="createdAtTo"
                    value={filters.createdAtTo}
                    onChange={handleFilterChange}
                    aria-label={t("vouchers:overview.createdToAriaLabel")}
                  />
                </div>
              </div>
            </div>

            <div className={`vo-filter-pair ${isQrMode ? "is-frozen" : ""}`}>
              <div
                className={`vo-filter-group vo-filter-redeem ${
                  isQrMode ? "is-frozen" : ""
                }`}
              >
                <div className="vo-filter-card vo-filter-card--date">
                  <label>{t("vouchers:overview.redeemDateLabel")}</label>
                  <div className="vo-filter-row">
                    <input
                      type="date"
                      name="redeemCreatedAtFrom"
                      value={filters.redeemCreatedAtFrom}
                      onChange={handleFilterChange}
                      aria-label={t("vouchers:overview.redeemCreatedFromAriaLabel")}
                      title={t("vouchers:overview.redeemDateTitle")}
                    />
                    <input
                      type="date"
                      name="redeemCreatedAtTo"
                      value={filters.redeemCreatedAtTo}
                      onChange={handleFilterChange}
                      aria-label={t("vouchers:overview.redeemCreatedToAriaLabel")}
                      title={t("vouchers:overview.redeemDateTitle")}
                    />
                  </div>
                </div>
              </div>

              <div
                className={`vo-filter-group vo-filter-exp ${
                  isQrMode ? "is-frozen" : ""
                }`}
              >
                <div className="vo-filter-card vo-filter-card--date vo-filter-card--date-weak">
                  <label>{t("vouchers:overview.expirationDateLabel")}</label>
                  <div className="vo-filter-row">
                    <input
                      type="date"
                      name="expirationDateFrom"
                      value={filters.expirationDateFrom}
                      onChange={handleFilterChange}
                      aria-label={t("vouchers:overview.expirationFromAriaLabel")}
                    />
                    <input
                      type="date"
                      name="expirationDateTo"
                      value={filters.expirationDateTo}
                      onChange={handleFilterChange}
                      aria-label={t("vouchers:overview.expirationToAriaLabel")}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div
              className={`vo-filter-group vo-filter-type ${
                isQrMode ? "is-frozen" : ""
              }`}
            >
              <label htmlFor="type">{t("vouchers:overview.typeLabel")}</label>
              <select
                id="type"
                name="type"
                value={filters.type}
                onChange={handleFilterChange}
              >
                <option value="">{t("vouchers:overview.anyOption")}</option>
                <option value="Usługa">{t("vouchers:types.Usługa")}</option>
                <option value="Kwota">{t("vouchers:types.Kwota")}</option>
              </select>
            </div>

            <div
              className={`vo-filter-group vo-filter-issuer ${
                isQrMode ? "is-frozen" : ""
              }`}
            >
              <label htmlFor="issuerSelect">{t("vouchers:overview.issuerLabel")}</label>
              <select
                id="issuerSelect"
                name="userIds"
                multiple
                value={filters.userIds}
                onChange={handleFilterChange}
                className="user-dropdown"
                aria-label={t("vouchers:overview.issuerLabel")}
              >
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.first_name} {u.last_name} ({u.username})
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Active filter chips */}
      {activeFilterChips.length > 0 && (
        <div
          className="vo-active-filters"
          role="region"
          aria-label={t("vouchers:overview.activeFiltersAriaLabel")}
        >
          <div className="vo-active-filters-row">
            <span className="vo-active-filters-title">{t("vouchers:overview.activeFiltersTitle")}</span>

            <div className="vo-chips" role="list" aria-label={t("vouchers:overview.filterChipsAriaLabel")}>
              {activeFilterChips.map((c) => (
                <span
                  key={c.key}
                  className={`vo-chip ${c.kind === "qr" ? "vo-chip--qr" : ""}`}
                  role="listitem"
                  title={c.label}
                >
                  <span className="vo-chip-text">{c.label}</span>
                  <button
                    type="button"
                    className={`vo-chip-x ${c.kind === "qr" ? "is-danger" : ""}`}
                    onClick={() => {
                      setPage(1);
                      c.onClear?.();
                    }}
                    aria-label={t("vouchers:overview.clearChipAriaLabel", {
                      label: c.label,
                    })}
                    title={t("vouchers:overview.clearChipTitle")}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>

            <button
              type="button"
              className="btn btn-ghost btn-xs vo-clear-all"
              onClick={resetFilters}
              title={t("vouchers:overview.resetAllFiltersTitle")}
            >
              {t("vouchers:overview.resetAllFiltersButton")}
            </button>
          </div>
        </div>
      )}

      {/* Table + pagination */}
      <div className="vo-table-wrap">
        <div
          className={`vo-table-scroller ${loading ? "is-loading" : ""}`}
          aria-busy={loading}
          ref={scrollerRef}
        >
          {(loading || qrLoading) && (
            <div className="vo-skeleton" aria-hidden="true">
              {Array.from({ length: 8 }).map((_, i) => (
                <div className="vo-skeleton-row" key={i} />
              ))}
            </div>
          )}

          <div className="vo-table-x" ref={tableWidthRef}>
            <table className="voucher-table" ref={tableRef}>
              <colgroup>
                {COL_KEYS.map((key) => (
                  <col key={key} width={widthAttrFor(key)} />
                ))}
              </colgroup>

              <thead>
                <tr>
                  {overviewTableColumns.map(({ key, label }) => (
                    <th
                      key={key}
                      className={key.startsWith("edit") || key === "redeem" || key === "print" || key === "history" || key === "moreActions" ? "col-action" : undefined}
                      style={{ width: widthFor(key) }}
                    >
                      <div className="th-inner">
                        <span className="th-label" title={label}>
                          {label}
                        </span>
                        <span
                          className="col-resizer"
                          title={t("vouchers:overview.resizeHandleTitle")}
                          onMouseDown={(e) => startResize(e, key)}
                          onDoubleClick={() => autoFitColumn(key)}
                        />
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {!loading && !qrLoading && paginated.length === 0 && (
                  <tr>
                    <td colSpan={COL_KEYS.length} className="vo-empty">
                      {isQrMode
                        ? t("vouchers:overview.emptyQrNoVoucher")
                        : selectedStatuses.length === 0
                        ? t("vouchers:overview.emptyNoStatuses")
                        : t("vouchers:overview.emptyNoVouchers")}
                    </td>
                  </tr>
                )}

                {paginated.map((voucher) => {
                  const statusText =
                    voucher.status ?? (voucher.open ? "Active" : "Archived");
                  const isArchived = statusText === "Archived";
                  const isDraft = statusText === "Draft";
                  const isCancelled = statusText === "Cancelled";

                  const canRedeem = statusText === "Active" && voucher.open === true;
                  const canEditToNote = voucher.open === true;
                  const canUnarchive = isSuperuser && isArchived;
                  const canSendEmail = !isCancelled;
                  const hasSecondaryActions = canSendEmail || !isArchived || canUnarchive;

                  const items = redeemHistory[voucher.id] ?? [];
                  const count =
                    historyCount[voucher.id] != null
                      ? historyCount[voucher.id]
                      : redeemHistory[voucher.id]?.length ?? undefined;
                  const hasHistory =
                    typeof count === "number" ? count > 0 : (items.length ?? 0) > 0;

                  const remaining = Number(voucher.remaining_balance);
                  const value = Number(voucher.value);
                  const low =
                    Number.isFinite(remaining) &&
                    Number.isFinite(value) &&
                    remaining > 0 &&
                    remaining <= value * 0.2;
                  const zero = Number.isFinite(remaining) && remaining === 0;

                  const rowCurrency = voucher.currency || "PLN";

                  return (
                    <React.Fragment key={voucher.id}>
                      <tr
                        data-voucher-id={voucher.id}
                        style={hasHistory ? { background: "#bfdbfe" } : undefined}
                      >
                        <td>
                          <div className="cell" style={cellWidthStyle("voucherNo")}>
                            {voucher.voucher_no}
                          </div>
                        </td>

                        <td>
                          <div className="cell" style={cellWidthStyle("voucherSearchNo")}>
                            {voucher.voucher_search_no}
                          </div>
                        </td>

                        <td>
                          <div className="cell" style={cellWidthStyle("category")}>
                            {t(`vouchers:categories.${voucher.category}`, {
                              defaultValue: voucher.category,
                            })}
                          </div>
                        </td>

                        <td>
                          <div className="cell" style={cellWidthStyle("type")}>
                            {t(`vouchers:types.${voucher.type}`, {
                              defaultValue: voucher.type,
                            })}
                          </div>
                        </td>

                        <td>
                          <div className="cell" style={cellWidthStyle("issuer")}>
                            {getIssuerName(voucher.issuer_id)}
                          </div>
                        </td>

                        <td>
                          <div className="cell" style={cellWidthStyle("value")}>
                            {Number(voucher.value).toFixed(2)} {rowCurrency}
                          </div>
                        </td>

                        <td>
                          <div
                            className={`cell ${zero ? "rem-zero" : low ? "rem-low" : ""}`}
                            style={cellWidthStyle("remaining")}
                          >
                            {Number(voucher.remaining_balance).toFixed(2)} {rowCurrency}
                          </div>
                        </td>

                        <td>
                          <div className="cell" style={cellWidthStyle("created")}>
                            {formatDate(voucher.createdAt)}
                          </div>
                        </td>

                        <td>
                          <div className="cell" style={cellWidthStyle("expires")}>
                            {formatDate(voucher.expiration_date)}
                          </div>
                        </td>

                        <td>
                          <div className="cell" style={cellWidthStyle("status")}>
                            <span className={`status-chip status-${statusText.toLowerCase()}`}>
                              {t(`vouchers:statuses.${statusText}`, {
                                defaultValue: statusText,
                              })}
                            </span>
                          </div>
                        </td>

                        <td>
                          <div className="cell" style={cellWidthStyle("toNote")}>
                            <label
                              title={
                                canEditToNote
                                  ? t("vouchers:overview.toggleToNoteTitle")
                                  : t("vouchers:overview.unavailableForStatusTitle")
                              }
                            >
                              <input
                                type="checkbox"
                                checked={!!voucher.to_note}
                                onChange={(e) =>
                                  handleToggleToNote(voucher, e.target.checked)
                                }
                                disabled={
                                  !canEditToNote || updatingToNoteId === voucher.id
                                }
                                aria-checked={!!voucher.to_note}
                              />
                              <span style={{ marginLeft: 6 }}>
                                {voucher.to_note
                                  ? t("vouchers:common.yes")
                                  : t("vouchers:common.no")}
                              </span>
                            </label>
                          </div>
                        </td>

                        <td>
                          <div className="cell" style={cellWidthStyle("comment")}>
                            {voucher.comment || ""}
                          </div>
                        </td>

                        <td className="td-action td-action-edit">
                          <div className="cell cell--action" style={cellWidthStyle("edit")}>
                            <button className="link-btn" onClick={() => handleEdit(voucher)}>
                              {t("common:actions.edit")}
                            </button>
                          </div>
                        </td>

                        <td className="td-action td-action-redeem">
                          <div className="cell cell--action" style={cellWidthStyle("redeem")}>
                            <button
                              className="link-btn success"
                              onClick={() => handleRedeem(voucher)}
                              disabled={!canRedeem}
                              aria-disabled={!canRedeem}
                              title={
                                canRedeem
                                  ? t("vouchers:overview.redeem")
                                  : isDraft
                                  ? t("vouchers:overview.cannotRedeemDraftTitle")
                                  : isCancelled
                                  ? t("vouchers:overview.cannotRedeemCancelledTitle")
                                  : t("vouchers:overview.cannotRedeemArchivedTitle")
                              }
                            >
                              {t("vouchers:overview.redeem")}
                            </button>
                          </div>
                        </td>

                        <td className="td-action td-action-print">
                          <div className="cell cell--action" style={cellWidthStyle("print")}>
                            <button
                              className="link-btn"
                              onClick={() => handleOpenPrintPreview(voucher)}
                            >
                              {t("vouchers:overview.print")}
                            </button>
                          </div>
                        </td>

                        <td className="td-action td-action-history">
                          <div className="cell cell--action" style={cellWidthStyle("history")}>
                            <button
                              className="link-btn muted"
                              onClick={() => handleRedeemHistoryToggle(voucher.id)}
                              aria-expanded={expandedVoucherId === voucher.id}
                              aria-controls={`history-${voucher.id}`}
                              title={t("vouchers:overview.historyTitle")}
                            >
                              {expandedVoucherId === voucher.id
                                ? t("vouchers:overview.hideHistoryButton", {
                                    count: count ?? "—",
                                  })
                                : t("vouchers:overview.showHistoryButton", {
                                    count: count ?? "—",
                                  })}
                            </button>
                          </div>
                        </td>

                        <td className="td-action td-action-more">
                          <div className="cell cell--action" style={cellWidthStyle("moreActions")}>
                            <button
                              className="link-btn muted"
                              onClick={() => handleMoreActionsToggle(voucher.id)}
                              disabled={!hasSecondaryActions}
                              aria-disabled={!hasSecondaryActions}
                              aria-expanded={expandedMoreActionsVoucherId === voucher.id}
                              aria-controls={`voucher-more-actions-${voucher.id}`}
                              title={
                                hasSecondaryActions
                                  ? t("vouchers:overview.moreActionsTitle")
                                  : t("vouchers:overview.noMoreActionsTitle")
                              }
                            >
                              {expandedMoreActionsVoucherId === voucher.id
                                ? t("vouchers:overview.hideMoreActionsButton")
                                : t("vouchers:overview.showMoreActionsButton")}
                            </button>
                          </div>
                        </td>
                      </tr>

                      {expandedMoreActionsVoucherId === voucher.id && (
                        <tr className="voucher-more-actions-row">
                          <td colSpan={COL_KEYS.length} id={`voucher-more-actions-${voucher.id}`}>
                            <div className="voucher-more-actions-container" aria-live="polite">
                              <div className="voucher-more-actions-title">
                                {t("vouchers:overview.moreActionsTitle")}
                              </div>

                              <div className="voucher-more-actions-list">
                                {canSendEmail && (
                                  <button
                                    type="button"
                                    className="link-btn voucher-more-action-btn"
                                    onClick={() => handleOpenEmailDialog(voucher)}
                                    disabled={sendingEmailId === voucher.id}
                                    aria-disabled={sendingEmailId === voucher.id}
                                    title={t("vouchers:overview.sendEmailTitle")}
                                  >
                                    {sendingEmailId === voucher.id
                                      ? t("vouchers:overview.sendingEmail")
                                      : t("vouchers:overview.sendEmail")}
                                  </button>
                                )}

                                {!isArchived && (
                                  <button
                                    className="link-btn danger voucher-more-action-btn"
                                    onClick={() => handleArchive(voucher.id)}
                                    disabled={archivingId === voucher.id}
                                    aria-disabled={archivingId === voucher.id}
                                    title={
                                      archivingId === voucher.id
                                        ? t("vouchers:overview.archiving")
                                        : t("vouchers:overview.archive")
                                    }
                                  >
                                    {archivingId === voucher.id
                                      ? t("vouchers:overview.archiving")
                                      : t("vouchers:overview.archive")}
                                  </button>
                                )}

                                {canUnarchive && (
                                  <button
                                    className="link-btn warning voucher-more-action-btn"
                                    onClick={() => handleUnarchive(voucher.id)}
                                    disabled={unarchivingId === voucher.id}
                                    aria-disabled={unarchivingId === voucher.id}
                                    title={
                                      unarchivingId === voucher.id
                                        ? t("vouchers:overview.unarchiving")
                                        : t("vouchers:overview.unarchive")
                                    }
                                  >
                                    {unarchivingId === voucher.id
                                      ? t("vouchers:overview.unarchiving")
                                      : t("vouchers:overview.unarchive")}
                                  </button>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}

                      {expandedVoucherId === voucher.id && (
                        <tr className="redeem-row">
                          <td colSpan={COL_KEYS.length} id={`history-${voucher.id}`}>
                            <div className="redeem-history-container" aria-live="polite">
                              <table className="redeem-history-table">
                                <thead>
                                  <tr>
                                    <th>{t("vouchers:redeem.reservationIdLabel")}</th>
                                    <th>{t("vouchers:redeem.redeemAmountLabel")}</th>
                                    <th>{t("vouchers:redeemEdit.notedLabel")}</th>
                                    <th>{t("vouchers:overview.redeemHistoryUserColumn")}</th>
                                    <th>{t("common:common.comment")}</th>
                                    <th>{t("vouchers:overview.createdAtLabel")}</th>
                                    <th>{t("common:actions.edit")}</th>
                                  </tr>
                                </thead>

                                <tbody>
                                  {(redeemHistory[voucher.id] ?? []).map((entry) => {
                                    const statusTextInner =
                                      voucher.status ??
                                      (voucher.open ? "Active" : "Archived");
                                    const canEditRedeem =
                                      statusTextInner === "Active" &&
                                      voucher.open === true;
                                    const entryCurrency = voucher.currency || "PLN";

                                    return (
                                      <tr key={entry.id} className="redeem-entry">
                                        <td>{entry.reservation_id}</td>
                                        <td>
                                          {Number(entry.redeemed_amount).toFixed(2)} {entryCurrency}
                                        </td>
                                        <td>
                                          <span
                                            className={`status-chip ${
                                              entry.noted
                                                ? "status-noted-yes"
                                                : "status-noted-no"
                                            }`}
                                          >
                                            {entry.noted
                                              ? t("vouchers:common.yes")
                                              : t("vouchers:common.no")}
                                          </span>
                                        </td>
                                        <td>{getIssuerName(entry.user_id)}</td>
                                        <td>{entry.comment || ""}</td>
                                        <td>{formatDate(entry.createdAt)}</td>
                                        <td>
                                          <button
                                            className="link-btn"
                                            onClick={() => handleEditRedeem(entry, voucher)}
                                            disabled={!canEditRedeem}
                                            aria-disabled={!canEditRedeem}
                                            title={
                                              canEditRedeem
                                                ? t("vouchers:overview.editRedeemEntryTitle")
                                                : t("vouchers:overview.redeemEditOnlyActiveTitle")
                                            }
                                          >
                                            {t("common:actions.edit")}
                                          </button>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <PaginationFooter
          className="vo-pagination"
          page={page}
          pageCount={totalPages}
          pageSize={pageSize}
          pageSizeOptions={[10, 25, 50]}
          totalRows={totalRows}
          totalLabel={t("vouchers:overview.totalRowsLabel", { count: totalRows })}
          onPageSizeChange={handlePageSizeChange}
          onPrev={goPrev}
          onNext={goNext}
          summary={
            <div className="vo-pagination-summary" aria-label={t("vouchers:overview.totalsAriaLabel")}>
              <div className="vo-total-group" title={t("vouchers:overview.pageTotalTitle")}>
                <span className="ttl-label">{t("vouchers:overview.pageTotalLabel")}</span>
                <span className="ttl-chip">
                  <em>{t("vouchers:overview.valueTotal")}</em>
                  <strong>{fmtAmount(pageValue)}</strong>
                </span>
                <span className="ttl-chip">
                  <em>{t("vouchers:overview.remainingTotal")}</em>
                  <strong>{fmtAmount(pageRemaining)}</strong>
                </span>
              </div>

              <div
                className="vo-total-group vo-total-grand"
                title={t("vouchers:overview.grandTotalTitle")}
              >
                <span className="ttl-label">{t("vouchers:overview.grandTotalLabel")}</span>
                <span className="ttl-chip">
                  <em>{t("vouchers:overview.valueTotal")}</em>
                  <strong>{fmtAmount(totalValue)}</strong>
                </span>
                <span className="ttl-chip">
                  <em>{t("vouchers:overview.remainingTotal")}</em>
                  <strong>{fmtAmount(totalRemaining)}</strong>
                </span>
              </div>
            </div>
          }
        />
      </div>

      {emailDialogOpen && emailVoucher && (
        <div
          className="vo-modal-backdrop"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !sendingEmailId) {
              closeEmailDialog();
            }
          }}
        >
          <div
            className="vo-email-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="voucher-email-dialog-title"
          >
            <div className="vo-email-dialog-header">
              <div>
                <h3 id="voucher-email-dialog-title">
                  {t("vouchers:overview.emailDialogTitle")}
                </h3>
                <p>
                  {t("vouchers:overview.emailDialogSubtitle", {
                    voucherNo: emailVoucher.voucher_no || emailVoucher.id,
                  })}
                </p>
              </div>
              <button
                type="button"
                className="vo-modal-close"
                onClick={closeEmailDialog}
                disabled={!!sendingEmailId}
                aria-label={t("common:actions.close")}
              >
                ×
              </button>
            </div>

            <div className="vo-email-dialog-body">
              {emailDefaultsLoading && (
                <div className="vo-email-defaults-note">
                  {t("vouchers:overview.emailDefaultsLoading")}
                </div>
              )}

              <label className="vo-email-field">
                <span>{t("vouchers:overview.emailToLabel")}</span>
                <input
                  type="email"
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  placeholder={t("vouchers:overview.emailToPlaceholder")}
                  disabled={!!sendingEmailId}
                />
              </label>

              <label className="vo-email-field">
                <span>{t("vouchers:overview.emailCcLabel")}</span>
                <input
                  type="text"
                  value={emailCc}
                  onChange={(e) => setEmailCc(e.target.value)}
                  placeholder={t("vouchers:overview.emailCcPlaceholder")}
                  disabled={!!sendingEmailId}
                />
              </label>

              <label className="vo-email-checkbox">
                <input
                  type="checkbox"
                  checked={emailAttachPdf}
                  onChange={(e) => setEmailAttachPdf(e.target.checked)}
                  disabled={!!sendingEmailId}
                />
                <span>{t("vouchers:overview.emailAttachPdfLabel")}</span>
              </label>

              <p className="vo-email-dialog-hint">
                {t("vouchers:overview.emailDialogHint")}
              </p>
            </div>

            <div className="vo-email-dialog-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeEmailDialog}
                disabled={!!sendingEmailId}
              >
                {t("common:actions.cancel")}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSendVoucherEmail}
                disabled={!!sendingEmailId || !String(emailTo || "").trim()}
              >
                {sendingEmailId
                  ? t("vouchers:overview.sendingEmail")
                  : t("vouchers:overview.sendEmail")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview / Print modal */}
      {previewOpen && previewData && (
        <div
          className="vo-preview-backdrop"
          onClick={(e) => {
            if (e.target.classList.contains("vo-preview-backdrop")) {
              handleClosePrintPreview();
            }
          }}
        >
          <div
            className="vo-preview-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={previewUiMeta.previewAriaLabel}
          >
            <button
              type="button"
              className="vo-preview-close"
              onClick={handleClosePrintPreview}
              aria-label={t("vouchers:overview.closePreviewAriaLabel")}
            >
              ×
            </button>

            <h2 className="vo-preview-title">{previewUiMeta.previewTitle}</h2>

            <p className="vo-preview-subtitle">
              {t("vouchers:overview.previewTechnicalNote")}
            </p>

            <div className="vo-preview-body">
              <div className="vo-preview-meta">
                {previewVoucherRow &&
                  (() => {
                    const currency = previewVoucherRow.currency || "PLN";
                    const businessMode =
                      previewData?.businessMode ||
                      getBusinessModeFromVoucherRow(previewVoucherRow);

                    const rawTemplateType =
                      previewVoucherRow.template_type ||
                      previewVoucherRow.templateType ||
                      previewData?.templateType ||
                      "—";

                    const rawTemplateVersion =
                      previewVoucherRow.template_version ||
                      previewVoucherRow.templateVersion ||
                      previewData?.templateVersion ||
                      "—";

                    const rawTheme =
                      previewVoucherRow.theme || previewData?.theme || "—";

                    return (
                      <>
                        <p>
                          <strong>{t("vouchers:overview.previewEntityNumberLabel", {
                            entity: previewUiMeta.singular,
                          })}</strong>{" "}
                          {previewVoucherRow.voucher_no}
                        </p>
                        <p>
                          <strong>{t("vouchers:overview.categoryLabel")}</strong>{" "}
                          {previewVoucherRow.category
                            ? t(`vouchers:categories.${previewVoucherRow.category}`, {
                                defaultValue: previewVoucherRow.category,
                              })
                            : "—"}
                        </p>
                        <p>
                          <strong>{t("vouchers:overview.businessModeLabel")}</strong>{" "}
                          {t(`vouchers:businessModes.${businessMode}`, {
                            defaultValue: businessMode,
                          })}
                        </p>
                        <p>
                          <strong>{t("vouchers:overview.valueLabel")}</strong>{" "}
                          {Number(previewVoucherRow.value).toFixed(2)} {currency}
                        </p>
                        <p>
                          <strong>{t("vouchers:overview.statusPreviewLabel")}</strong>{" "}
                          {t(
                            `vouchers:statuses.${
                              previewVoucherRow.status ??
                              (previewVoucherRow.open ? "Active" : "Archived")
                            }`,
                            {
                              defaultValue:
                                previewVoucherRow.status ??
                                (previewVoucherRow.open ? "Active" : "Archived"),
                            }
                          )}
                        </p>
                        <p>
                          <strong>{t("vouchers:overview.templateTypeLabel")}</strong> {rawTemplateType}
                        </p>
                        <p>
                          <strong>{t("vouchers:overview.templateVersionLabel")}</strong> {rawTemplateVersion}
                        </p>
                        <p>
                          <strong>{t("vouchers:overview.themeLabel")}</strong> {rawTheme}
                        </p>
                        {previewVoucherRow.expiration_date && (
                          <p>
                            <strong>{t("vouchers:overview.expiresLabel")}</strong>{" "}
                            {formatDate(previewVoucherRow.expiration_date)}
                          </p>
                        )}
                      </>
                    );
                  })()}

                <p className="vo-preview-note">
                  {t("vouchers:overview.previewPdfHint")}
                </p>
              </div>

              <div className="vo-preview-voucher-wrapper">
                <VoucherPreview preview={previewData} />
              </div>
            </div>

            <div className="vo-preview-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleClosePrintPreview}
              >
                {t("common:actions.close")}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handlePrintPdf}
                disabled={printing || !previewVoucherRow}
              >
                {printing
                  ? t("vouchers:overview.openingPdf")
                  : t("vouchers:overview.openPdfPrint")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

